import React, { useState } from 'react';
import { PILLARS_DATA, Pillar } from './archiveData';

export const SixPillars: React.FC = () => {
  const [activePillarId, setActivePillarId] = useState<number>(1);
  const [copied, setCopied] = useState<boolean>(false);

  const activePillar: Pillar = PILLARS_DATA.find(p => p.id === activePillarId) || PILLARS_DATA[0];

  const handleCopyInvocation = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(activePillar.invocation);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = activePillar.invocation;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback ignore
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" id="six-pillars-section">
      <div className="border-b border-iron-dark pb-4">
        <div className="flex items-center gap-2 text-xs font-mono text-ember-spark uppercase tracking-widest">
          <span>LIBER PRIMUS</span>
          <span>•</span>
          <span>THE SIX PILLARS</span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-parchment-light uppercase mt-1">
          The Six Pillars of the Earth King
        </h2>
        <p className="text-xs sm:text-sm text-parchment-dim mt-2 max-w-3xl">
          The foundational cosmology, metaphysics, and operative ethics of King Belial: the unbroken bedrock upon which the Sovereign Self is built.
        </p>
      </div>

      {/* Pillar Selection Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3" role="tablist" aria-label="Six Pillars of Belial">
        {PILLARS_DATA.map((pillar) => {
          const isActive = pillar.id === activePillarId;
          return (
            <button
              key={pillar.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`pillar-panel-${pillar.id}`}
              id={`pillar-tab-${pillar.id}`}
              onClick={() => {
                setActivePillarId(pillar.id);
                setCopied(false);
              }}
              className={`p-3 rounded border text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright ${
                isActive
                  ? 'border-gold-bright bg-obsidian-surface text-gold-bright shadow-glow-gold'
                  : 'border-iron-dark bg-obsidian-deep/80 text-parchment-dim hover:border-iron-base hover:text-parchment-light'
              }`}
            >
              <div className="text-[10px] font-mono text-gold-ancient/80 uppercase">PILLAR {pillar.roman}</div>
              <div className="font-display text-xs sm:text-sm font-semibold truncate">{pillar.title}</div>
            </button>
          );
        })}
      </div>

      {/* Active Pillar Full Exegesis Card */}
      <article
        role="tabpanel"
        id={`pillar-panel-${activePillar.id}`}
        aria-labelledby={`pillar-tab-${activePillar.id}`}
        className="chthonic-card rounded-lg p-6 sm:p-8 lg:p-10 space-y-8"
      >
        {/* Header Metadata */}
        <div className="space-y-3 border-b border-iron-dark/80 pb-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-mono text-gold-ancient tracking-widest uppercase">
              AXIS {activePillar.roman} • {activePillar.hebrewTitle}
            </span>
            <span className="text-[11px] font-serif italic text-iron-steel">
              {activePillar.occultTitle}
            </span>
          </div>

          <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-parchment-light">
            {activePillar.title}
          </h3>

          <div className="font-serif text-sm sm:text-base text-gold-bright italic">
            {activePillar.latinTitle}
          </div>

          {activePillar.etymology && (
            <div className="rounded border border-iron-dark/60 bg-obsidian-deep/60 p-3 text-xs text-parchment-dim/90 font-serif leading-relaxed">
              <strong className="text-gold-ancient font-mono text-[10px] uppercase block mb-1">Etymological Foundation:</strong>
              {activePillar.etymology}
            </div>
          )}
        </div>

        {/* Philosophical Exegesis */}
        <div className="space-y-4">
          <h4 className="font-display text-xs uppercase tracking-widest text-gold-ancient">
            Philosophical Exegesis
          </h4>
          <div className="space-y-4 text-xs sm:text-sm text-parchment-base leading-relaxed">
            {activePillar.exegesis.map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
        </div>

        {/* Liturgical Invocation */}
        <div className="space-y-3 rounded-lg border border-bronze-dark/60 bg-obsidian-deep/90 p-5 sm:p-6 shadow-chthonic">
          <div className="flex items-center justify-between gap-4 border-b border-iron-dark/60 pb-3">
            <div className="font-display text-xs uppercase tracking-widest text-gold-bright">
              {activePillar.invocationTitle}
            </div>
            <button
              onClick={handleCopyInvocation}
              className="rounded border border-iron-base bg-obsidian-surface px-3 py-1 text-[11px] font-mono text-gold-ancient hover:border-gold-bright hover:text-gold-bright transition-colors"
            >
              {copied ? 'COPIED TO RECORD' : 'COPY INVOCATION'}
            </button>
          </div>

          <pre className="whitespace-pre-wrap font-serif text-xs sm:text-sm text-parchment-light italic leading-relaxed pt-2">
            {activePillar.invocation}
          </pre>
        </div>

        {/* Operative Meditative Formula */}
        <div className="space-y-2 rounded-lg border border-iron-dark bg-obsidian-surface/60 p-5 sm:p-6">
          <div className="text-xs font-display uppercase tracking-widest text-ember-spark">
            Operative Meditative Formula: {activePillar.formulaTitle}
          </div>
          <p className="text-xs sm:text-sm text-parchment-dim leading-relaxed font-serif">
            {activePillar.formula}
          </p>
        </div>
      </article>
    </div>
  );
};

export default SixPillars;
